import React, { useMemo } from 'react'
import { Flex, Grid, Group, Paper, Stack, Text, useMantineTheme } from '@mantine/core'
import { useParams } from 'react-router'
import Loading from './../../../react/core/Loading'
import ApplyButton from './../../components/ApplyButton'
import { IconMapPin } from '@tabler/icons-react'
import Badges from '../../components/Badges'
import NotFound from '../../../react/core/NotFound'
import ScrollToTop from '../../../components/ScrollToTop'
import DOMPurify from 'dompurify'
import { useGetJobBoardJobPostingQuery } from '../redux/query/jobBoardApi.slice'
import Banner from '../components/Banner'
import JobBoardRecentJobs from '../components/JobBoardRecentJobs'
import { useMediaQuery } from '@mantine/hooks'

const WIDTH = { base: '90%', md: '80%', lg: '60%' }
const MAX_WIDTH = '1170px'
const JOB_POSTING_COLS = { base: 12, md: 8 }
const ASIDE_COLS = { base: 12, md: 4 }

/**
 * Renders the job posting page.
 * @returns {JSX.Element}
 */
export default function JobsJobPosting () {
  const { identifier } = useParams()
  const theme = useMantineTheme()
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const { data: jobPosting, error, isLoading } = useGetJobBoardJobPostingQuery({ identifier })

  const sanitizedDescription = useMemo(() => {
    return DOMPurify.sanitize(jobPosting?.description ?? '')
  }, [jobPosting])

  if (isLoading) {
    return <Loading />
  }

  document.title = jobPosting?.title ?? 'Job listing expired'

  return (
    <>
      <ScrollToTop />
      <Flex
        gap='md'
        justify='center'
        align='center'
        direction='column'
        wrap='wrap'
        h='100%'
        pb='5em'
      >
        <Banner color='#2A2B2A'>
          <Stack align='flex-start' justify='center' w={WIDTH} maw={MAX_WIDTH}>
            <Text c='white' size={isSmallScreen ? '24px' : '48px'} aria-label='Job posting title'>
              {!error && jobPosting?.title}
            </Text>
            <Group>
              {!error && <Badges jobPosting={jobPosting} />}
              {!error && <Text c='white' size={isSmallScreen ? 'md' : 'lg'}><IconMapPin size={isSmallScreen ? 20 : 24} aria-label='Location of the job post' /> {jobPosting.location}</Text>}
            </Group>
          </Stack>
        </Banner>
        <Grid w={WIDTH} maw={MAX_WIDTH} mt='-9.5vh'>
          <Grid.Col span={JOB_POSTING_COLS}>
            <Paper p='xl'>
              {
                !error &&
                <Stack>
                  <Text dangerouslySetInnerHTML={{ __html: sanitizedDescription }}></Text>
                  <ApplyButton color='blue' href={jobPosting.apply_url} size={isSmallScreen ? 'sm' : 'xl'} />
                </Stack>
              }
              {error?.originalStatus === 404 && <NotFound height='100%' title='' message='This job listing has expired' />}
            </Paper>
          </Grid.Col>
          <Grid.Col span={ASIDE_COLS}>
            <Stack>
              {!error && <ApplyButton color='blue' href={jobPosting.apply_url} visibleFrom='md' />}
              <JobBoardRecentJobs />
            </Stack>
          </Grid.Col>
        </Grid>
      </Flex>
    </>
  )
}
